export const DEFAULT_ANNUAL_TAX_RULE = Object.freeze({
  fundThreshold: 5000000,
  fundRate: 0.03,
  fundCap: 100000000,
  fundReminderStartMonth: 8,
  ymmThreshold: 500000,
  soleDeclarationMonth: 3,
  corporateDeclarationMonth: 4,
  ymmDueOffsetMonths: 2
});

const pad = (value) => String(value).padStart(2, "0");

export const isoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const endOfMonthIso = (year, month) => isoDate(new Date(year, month, 0));

export const normalizeAnnualTaxRule = (rule = {}) => ({
  ...DEFAULT_ANNUAL_TAX_RULE,
  ...rule,
  fundThreshold: Number(rule.fundThreshold ?? DEFAULT_ANNUAL_TAX_RULE.fundThreshold),
  fundRate: Number(rule.fundRate ?? DEFAULT_ANNUAL_TAX_RULE.fundRate),
  fundCap: Number(rule.fundCap ?? DEFAULT_ANNUAL_TAX_RULE.fundCap),
  fundReminderStartMonth: Number(rule.fundReminderStartMonth ?? DEFAULT_ANNUAL_TAX_RULE.fundReminderStartMonth),
  ymmThreshold: Number(rule.ymmThreshold ?? DEFAULT_ANNUAL_TAX_RULE.ymmThreshold),
  soleDeclarationMonth: Number(rule.soleDeclarationMonth ?? DEFAULT_ANNUAL_TAX_RULE.soleDeclarationMonth),
  corporateDeclarationMonth: Number(rule.corporateDeclarationMonth ?? DEFAULT_ANNUAL_TAX_RULE.corporateDeclarationMonth),
  ymmDueOffsetMonths: Number(rule.ymmDueOffsetMonths ?? DEFAULT_ANNUAL_TAX_RULE.ymmDueOffsetMonths)
});

export const defaultDeclarationDate = ({ fiscalYear, taxpayerType, rule }) => {
  const normalizedRule = normalizeAnnualTaxRule(rule);
  const declarationYear = Number(fiscalYear) + 1;
  const month = taxpayerType === "sole"
    ? normalizedRule.soleDeclarationMonth
    : normalizedRule.corporateDeclarationMonth;
  return endOfMonthIso(declarationYear, month);
};

export const calculateAnnualTaxObligation = ({
  fiscalYear,
  taxpayerType = "corporate",
  exemptionAmount = 0,
  declarationDate = "",
  fundDueDateOverride = "",
  ymmDueDateOverride = "",
  rule = {}
}) => {
  const normalizedRule = normalizeAnnualTaxRule(rule);
  const safeFiscalYear = Number(fiscalYear) || new Date().getFullYear() - 1;
  const safeAmount = Math.max(0, Number(exemptionAmount) || 0);
  const resolvedDeclarationDate = declarationDate || defaultDeclarationDate({
    fiscalYear: safeFiscalYear,
    taxpayerType,
    rule: normalizedRule
  });
  const declarationYear = Number(resolvedDeclarationDate.slice(0, 4)) || safeFiscalYear + 1;
  const declarationMonth = Number(resolvedDeclarationDate.slice(5, 7)) || (
    taxpayerType === "sole"
      ? normalizedRule.soleDeclarationMonth
      : normalizedRule.corporateDeclarationMonth
  );

  const fundRequired = safeAmount >= normalizedRule.fundThreshold;
  const fundAmount = fundRequired
    ? Math.min(safeAmount * normalizedRule.fundRate, normalizedRule.fundCap)
    : 0;
  const fundDueDate = fundDueDateOverride || `${declarationYear}-12-31`;

  const ymmRequired = safeAmount > normalizedRule.ymmThreshold;
  const ymmDueMonthDate = new Date(declarationYear, declarationMonth - 1 + normalizedRule.ymmDueOffsetMonths + 1, 0);
  const ymmDueDate = ymmDueDateOverride || isoDate(ymmDueMonthDate);

  return {
    fiscalYear: safeFiscalYear,
    declarationYear,
    declarationDate: resolvedDeclarationDate,
    exemptionAmount: safeAmount,
    fundRequired,
    fundAmount,
    fundDueDate,
    ymmRequired,
    ymmDueDate,
    ruleSnapshot: normalizedRule
  };
};

export const annualObligationStatus = ({ required, status, dueDate }) => {
  if (!required) return "not_applicable";
  if (status === "completed") return "completed";
  if (!dueDate) return "pending";
  const today = isoDate(new Date());
  return dueDate < today ? "overdue" : "pending";
};

